import { GRID_ROW_NUMBER_COL_DEF } from '@repo/go-table';
import { format, getUnixTime, parse } from 'date-fns';
import { action, computed, makeObservable, observable } from 'mobx';

import { normalizeColorToHex } from 'src/components/controls/color-picker-component/utils';
import { hasValue } from 'src/packages/utils/has-value';

import type {
  GridApi,
  GridRowSelectionModel,
  GridCellSelectionModel,
  GridRowId,
  TValueDraggingEndEvent,
  GridRowModelUpdate,
} from '@repo/go-table';
import type { MutableRefObject } from 'react';
import type { TPrimitive, TPrimitiveArray } from 'src/packages/types';
import type { EventEmitter } from 'src/packages/utils/event-emitter';
import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { TMainDirectoryEvents } from 'src/services/directory-service/main-directory-service';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type { TControlView, TRecord } from 'src/services/directory-service/types';
import type { LocalizationService } from 'src/services/localization-service.ts';
import type { NotificationsService } from 'src/services/notifications-service';
import type { TableSettingsManager } from 'src/services/table-settings-service/table-settings-manager';

import { ACTIVITY_COLUMN_FIELD, CHECKBOX_SELECTION_COLUMN_FIELD, RECORD_BUTTONS_COLUMN } from './consts';

const TABLE_SETTINGS_ERROR_MESSAGES = ['No filter operator found for column'];

const NON_DATA_COLUMN_FIELDS = [
  GRID_ROW_NUMBER_COL_DEF.field,
  ACTIVITY_COLUMN_FIELD,
  CHECKBOX_SELECTION_COLUMN_FIELD,
  RECORD_BUTTONS_COLUMN,
];

type TCellCoordinates = { cellField: string; rowId: number };
type TCellSelectionModel = Record<number, Record<string, boolean>>;

interface IDirectoryService {
  controlsViewsMap: Map<string, TControlView>;
  recordControlsViews: TControlView[];
  recordsMap: Map<string | number, DirectoryRecord>;
  optionsService: OptionsService;
  generalDataValues: Record<string, unknown>;
  eventEmitter: EventEmitter<TMainDirectoryEvents>;
  tableSettingsManager: TableSettingsManager;
  onValueChange(record: DirectoryRecord, fieldId: string, value: unknown): void;
  createRecords(initialValues: Partial<TRecord>[]): DirectoryRecord[];
}

const convertExcelStringToStringArrays = (text: string): (string | null)[][] => {
  const rawRows = text.replaceAll('\r', '').split('\n');

  const rows: (string | null)[][] = [];

  for (const row of rawRows) {
    const values: (string | null)[] = [];

    for (const value of row.split('\t')) {
      if (value !== '' && value !== '-') {
        values.push(value);
      } else {
        values.push(null);
      }
    }

    rows.push(values);
  }

  return rows;
};

export class DirectoryTableMediator {
  private readonly directory: IDirectoryService;
  private readonly notifications: NotificationsService;
  private readonly localization: LocalizationService;
  private readonly tableApi: MutableRefObject<GridApi>;

  @observable isCopyAvailable = false;
  @observable selectedRows: GridRowSelectionModel = [];

  constructor(
    directory: IDirectoryService,
    tableApi: MutableRefObject<GridApi>,
    notifications: NotificationsService,
    localization: LocalizationService,
  ) {
    this.tableApi = tableApi;
    this.directory = directory;
    this.notifications = notifications;
    this.localization = localization;

    makeObservable(this);
  }

  @action.bound
  updateSelectedRows(model: GridRowSelectionModel): void {
    this.selectedRows = model;
  }

  @computed
  get generalDataValues(): Record<string, unknown> {
    return this.directory.generalDataValues;
  }

  private parseTextToControlValue(
    controlView: TControlView,
    value: string | null,
  ): TPrimitive | TPrimitiveArray | TPrimitive[] | null {
    if (!hasValue(value)) {
      return null;
    }

    switch (controlView.control) {
      case 'CheckBox': {
        const yesInDiffLangs = Object.values(this.localization.getCertainLocaleInAllLanguages('common:yes')).map(
          (val) => val.toLowerCase(),
        );

        return yesInDiffLangs.includes(value.toLowerCase());
      }

      case 'ComboBox': {
        const options = [
          ...(this.directory.optionsService.options[controlView.fieldId] ?? []),
          ...(this.directory.optionsService.archivedOptions[controlView.fieldId] ?? []),
        ];

        const option = options?.find((opt) => opt.label.toLowerCase() === value.toLowerCase());

        return option?.value ?? null;
      }

      case 'MultiComboBox': {
        const options = [
          ...(this.directory.optionsService.options[controlView.fieldId] ?? []),
          ...(this.directory.optionsService.archivedOptions[controlView.fieldId] ?? []),
        ];

        const labels = value.split(',').map((val) => val.trim());
        const values: (string | number | boolean)[] = [];

        labels.forEach((label) => {
          const correspondingOption = options.find((opt) => opt.label.toLowerCase() === label.toLowerCase());

          if (correspondingOption) {
            values.push(correspondingOption.value);
          }
        });

        return values;
      }

      case 'StringsList': {
        return value.split(',').map((val) => val.trim());
      }

      case 'BitmapImage':
      case 'Field': {
        if (controlView.type === 'String') {
          return value;
        } else {
          const formattedValue = value.replaceAll(/\s+/g, '').replaceAll(/,/g, '.');

          const numberValue = Number(formattedValue);

          return Number.isNaN(numberValue) ? null : numberValue;
        }
      }

      case 'Label': {
        const isSimpleLabel = !('refObjectType' in controlView);

        if (isSimpleLabel) {
          return value;
        }

        const options = [
          ...(this.directory.optionsService.options[controlView.fieldId] ?? []),
          ...(this.directory.optionsService.archivedOptions[controlView.fieldId] ?? []),
        ];

        const option = options?.find((opt) => opt.label.toLowerCase() === value.toLowerCase());

        return option?.value ?? null;
      }

      case 'DateOnlyPicker': {
        const unixValue = getUnixTime(parse(value, 'dd.MM.yyyy', new Date()));

        return Number.isNaN(unixValue) ? null : unixValue;
      }

      case 'YearOnlyPicker': {
        const numberValue = Number(value);

        return Number.isNaN(numberValue) ? null : numberValue;
      }

      case 'ColorPicker': {
        return normalizeColorToHex(value);
      }

      default: {
        return null;
      }
    }
  }

  private parseControlValueToText(fieldView: TControlView, value: unknown): string {
    if (fieldView.control === 'StringsList') {
      if (!Array.isArray(value)) {
        return '-';
      }

      return value.join(', ');
    }

    if (fieldView.control === 'ComboBox') {
      if (!hasValue(value)) {
        return '-';
      }

      if (typeof value !== 'number' && typeof value !== 'string') {
        console.error('wrong type value');
        return '-';
      }

      const option =
        this.directory.optionsService.optionsMap[fieldView.fieldId]?.get(value) ??
        this.directory.optionsService.archivedOptionsMap[fieldView.fieldId]?.get(value);

      return option?.label ?? '-';
    }

    if (fieldView.control === 'MultiComboBox') {
      if (!Array.isArray(value)) {
        return '-';
      }

      const labels: string[] = [];

      value.forEach((v) => {
        const option =
          this.directory.optionsService.optionsMap[fieldView.fieldId]?.get(v) ??
          this.directory.optionsService.archivedOptionsMap[fieldView.fieldId]?.get(v);

        if (option) {
          labels.push(option.label);
        }
      });

      return labels.join(', ') || '-';
    }

    if (fieldView.control === 'CheckBox') {
      return this.localization.t(`common:${!!value ? 'yes' : 'no'}`);
    }

    if (fieldView.control === 'Label') {
      if (!hasValue(value)) {
        return '-';
      }

      const isSimpleLabel = !('refObjectType' in fieldView);

      if (isSimpleLabel) {
        return value?.toString() ?? '-';
      }

      if (typeof value !== 'number' && typeof value !== 'string') {
        console.error('wrong type value');
        return '-';
      }

      const option =
        this.directory.optionsService.optionsMap[fieldView.fieldId]?.get(value) ??
        this.directory.optionsService.archivedOptionsMap[fieldView.fieldId]?.get(value);

      return option?.label ?? '-';
    }

    if (fieldView.control === 'DateOnlyPicker') {
      if (!hasValue(value)) {
        return '-';
      }

      if (typeof value !== 'number') {
        console.error('wrong type value');
        return '-';
      }

      return format(value * 1000, 'dd.MM.yyyy');
    }

    if (fieldView.control === 'YearOnlyPicker') {
      return value?.toString() ?? '-';
    }

    if (fieldView.control === 'ColorPicker') {
      if (typeof value !== 'string') {
        return '-';
      }

      return normalizeColorToHex(value) ?? '-';
    }

    if (fieldView.control === 'Field' || fieldView.control === 'BitmapImage') {
      if (!hasValue(value) || value === '' || (typeof value !== 'number' && typeof value !== 'string')) {
        return '-';
      }

      return value.toString();
    }

    return '-';
  }

  @action.bound
  onControlValueChange(recordId: number | string, fieldId: string, value: unknown): void {
    const record = this.directory.recordsMap.get(recordId);

    if (!record) {
      return;
    }

    this.directory.onValueChange(record, fieldId, value);
  }

  @action.bound
  async pasteData(tablePasteTarget?: TCellCoordinates): Promise<void> {
    const api = this.tableApi.current;
    let clipboardData = '';

    try {
      clipboardData = await navigator.clipboard.readText();
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('common:errors.blockedCopyPaste');
      return;
    }

    if (!clipboardData || typeof clipboardData !== 'string') {
      return;
    }

    const columns = api.getVisibleColumns();
    const startPasteColumnIndex = columns.findIndex((col) => col.field === tablePasteTarget?.cellField);
    const visibleColumns = startPasteColumnIndex > 0 ? columns.slice(startPasteColumnIndex) : columns;

    const rowsVisibility = api.state.filter.filteredRowsLookup;

    const filteredColumns = visibleColumns.filter((col) => !NON_DATA_COLUMN_FIELDS.includes(col.field));
    const existingRows = (() => {
      if (!tablePasteTarget) {
        return [];
      }

      const allRows = api.getSortedRows();
      const startPasteRowIndex = allRows.findIndex((row) => row.recordId === tablePasteTarget.rowId);

      if (startPasteRowIndex < 0) {
        return [];
      }

      return allRows.slice(startPasteRowIndex).filter((row) => rowsVisibility[row.recordId]) as DirectoryRecord[];
    })();

    const initialDataRows = convertExcelStringToStringArrays(clipboardData).filter(
      (row) => !row.every((val) => val === null),
    );

    const updatedRows: DirectoryRecord[] = [];
    const newRecordsValues: Partial<TRecord>[] = [];

    initialDataRows.forEach((row, index) => {
      const existingRow = existingRows[index];
      const initialRowValue: Partial<TRecord> = {};

      for (let i = 0; i < row.length; i++) {
        const value = row[i] ?? null;

        const fieldId = filteredColumns[i]?.field;

        if (!fieldId) {
          console.error('cannot get fieldId');
          continue;
        }

        const controlView = this.directory.controlsViewsMap.get(fieldId);

        if (!controlView) {
          console.error('cannot find corresponding fieldView');
          continue;
        }

        const parsedValue = this.parseTextToControlValue(controlView, value);

        if (existingRow) {
          const record = this.directory.recordsMap.get(existingRow.recordId);

          if (record) {
            this.directory.onValueChange(record, fieldId, parsedValue);
          } else {
            console.warn('corresponding control is not presented for given attrName', fieldId);
          }
        } else {
          initialRowValue[fieldId] = parsedValue;
        }
      }

      if (existingRow) {
        updatedRows.push(existingRow);
      } else {
        newRecordsValues.push(initialRowValue);
      }
    });

    const newRecords = this.directory.createRecords(newRecordsValues);

    api.updateRows([...updatedRows, ...newRecords]);

    const lastNewRecord = newRecords.at(-1);

    if (lastNewRecord) {
      setTimeout(() => {
        api.scrollToIndexes({ rowIndex: api.getRowIndexRelativeToVisibleRows(lastNewRecord.recordId) });
      }, 300);
    }
  }

  @action.bound
  setIsCopyAvailable(isAvailable: boolean): void {
    this.isCopyAvailable = isAvailable;
  }

  copySelectedCellData = async (selectedCellData: TCellSelectionModel) => {
    const api = this.tableApi.current;

    const dataRows: string[] = [];

    for (const rowId in selectedCellData) {
      const parsedRowId = Number.isNaN(Number(rowId)) ? rowId : Number(rowId);

      const row = this.directory.recordsMap.get(parsedRowId);

      if (!row) {
        console.error('cannot find correspnding record');
        continue;
      }

      const values: string[] = [];

      for (const fieldId in selectedCellData[rowId]) {
        const fieldView = this.directory.controlsViewsMap.get(fieldId);
        const fieldValue = row.data[fieldId];

        if (!fieldView) {
          console.warn(`cannot find field view for selected cell. rowId: ${rowId}, fieldId: ${fieldId}`);
          values.push('-');
          continue;
        }

        values.push(this.parseControlValueToText(fieldView, fieldValue));
      }

      dataRows.push(values.join('\t'));
    }

    const copyData = dataRows.join('\n');

    try {
      await navigator.clipboard.writeText(copyData);

      api.setRowSelectionModel([]);
      this.notifications.showSuccessMessageT('common:dataCopied');
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('common:errors.blockedCopyPaste');
    }
  };

  copySelectedRowsToClipboard = async () => {
    const api = this.tableApi.current;
    const columnsOrderModel = this.directory.tableSettingsManager?.settings.columnsOrderModel?.filter(
      (fieldId) => !NON_DATA_COLUMN_FIELDS.includes(fieldId),
    );

    if (!columnsOrderModel) {
      console.error('columnsOrderModel is not presented!');
      return;
    }

    const rowsVisibility = api.state.filter.filteredRowsLookup;
    const columnsVisibility = api.state.columns.columnVisibilityModel;

    const sortedRows = api.getSortedRows();
    const selectedRows = api.getSelectedRows();
    const filteredRows = sortedRows.filter((row) => selectedRows.get(row.recordId) && rowsVisibility[row.recordId]);

    const copyData = filteredRows
      .map((row) => {
        const values: (string | number)[] = [];

        for (const fieldView of this.directory.recordControlsViews) {
          if (columnsVisibility[fieldView.fieldId] === false) {
            continue;
          }

          values.push(this.parseControlValueToText(fieldView, row.data[fieldView.fieldId]));
        }

        return values.join('\t');
      })
      .join('\n');

    try {
      await navigator.clipboard.writeText(copyData);

      api.setRowSelectionModel([]);
      this.directory.tableSettingsManager?.setSelectedCellData(null);
      this.notifications.showSuccessMessageT('common:dataCopied');
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('common:errors.blockedCopyPaste');
    }
  };

  @action.bound
  handleValueDraggingEnd(
    model: GridCellSelectionModel,
    [sourceRowId, sourceFieldName]: [GridRowId, string],
    event: TValueDraggingEndEvent,
  ): void {
    event.prevented = true;

    if (typeof sourceRowId !== 'number') {
      return;
    }

    const rowInst = this.directory.recordsMap.get(sourceRowId);

    if (!rowInst) {
      return;
    }

    const cellValue = rowInst.data[sourceFieldName];

    if (!hasValue(cellValue)) {
      return;
    }

    const rowsToUpdate: DirectoryRecord[] = [];

    for (const rowId in model) {
      const parsedRowId = Number(rowId);
      if (Number.isNaN(parsedRowId)) {
        continue;
      }

      const row = this.directory.recordsMap.get(parsedRowId);

      if (!row) {
        continue;
      }

      rowsToUpdate.push(row);

      for (const fieldId in model[parsedRowId]) {
        const record = this.directory.recordsMap.get(parsedRowId);

        if (!record) {
          continue;
        }

        this.directory.onValueChange(record, fieldId, cellValue);
      }
    }

    this.tableApi.current.updateRows(rowsToUpdate);
  }

  onDeleteRecord(record: DirectoryRecord): void {
    const api = this.tableApi.current;
    api.updateRows([{ recordId: record.recordId, _action: 'delete' }]);
  }

  onAddNewRecord(record: DirectoryRecord): void {
    const api = this.tableApi.current;
    const firstEditableCell = api.getAllColumns().find((col) => col.editable);

    if (api.state.filter.filterModel.items.length || api.state.sorting.sortModel.length) {
      api.setFilterModel({ items: [] });
      api.setSortModel([]);
      this.notifications.showWarningMessageT('directory:notifications.resetFiltersOnNewRecord');
    }

    api.updateRows([record]);

    if (firstEditableCell) {
      api.setCellFocus(record.recordId, firstEditableCell.field);

      if (firstEditableCell.type !== 'boolean') {
        api.startCellEditMode({ id: record.recordId, field: firstEditableCell.field });
      }
    }

    setTimeout(() => {
      api.scrollToIndexes({ rowIndex: api.getRowIndexRelativeToVisibleRows(record.recordId) });
    }, 300);
  }

  private isTableErrorWithSettings(error: Error): boolean {
    const message = error.message;

    if (!message) {
      return false;
    }

    return TABLE_SETTINGS_ERROR_MESSAGES.some((em) => message.includes(em));
  }

  onError = (error: Error) => {
    if (this.isTableErrorWithSettings(error)) {
      this.directory.tableSettingsManager.resetSettings();
    }
  };

  private onUpdateRecord({ record }: { record: DirectoryRecord }): void {
    this.tableApi.current.updateRows([record]);
  }

  private onNewRecordsList(records: DirectoryRecord[]): void {
    const oldIds: GridRowModelUpdate[] = this.tableApi.current
      .getSortedRows()
      .map((oldRec) => ({ ...oldRec, _action: 'delete' }));
    this.tableApi.current.updateRows([...oldIds, ...records]);
  }

  init = () => {
    const onUpdateRecordDisposer = this.directory.eventEmitter.addEventListener(
      'recordDataUpdated',
      this.onUpdateRecord.bind(this),
    );

    const onNewRecordsListDisposer = this.directory.eventEmitter.addEventListener(
      'newRecordsList',
      this.onNewRecordsList.bind(this),
    );

    return () => {
      onUpdateRecordDisposer();
      onNewRecordsListDisposer();
    };
  };
}

export { ACTIVITY_COLUMN_FIELD, CHECKBOX_SELECTION_COLUMN_FIELD };
